/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Reusable Data Table Pagination Component (Task 11.1.3)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useI18n } from '../../i18n';

export interface DataTablePaginationProps {
  pageIndex: number;
  pageSize: number;
  totalCount: number;
  pageCount: number;
  canPreviousPage: boolean;
  canNextPage: boolean;
  onPageChange: (newPage: number) => void;
  onPageSizeChange?: (newSize: number) => void;
  pageSizeOptions?: number[];
}

export const DataTablePagination: React.FC<DataTablePaginationProps> = ({
  pageIndex,
  pageSize,
  totalCount,
  pageCount,
  canPreviousPage,
  canNextPage,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
}) => {
  const { t } = useI18n();

  const startEntry = totalCount === 0 ? 0 : pageIndex * pageSize + 1;
  const endEntry = Math.min((pageIndex + 1) * pageSize, totalCount);

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-3 border-t border-surface-border bg-surface text-xs text-muted-fg">
      <div className="flex items-center gap-2">
        <span>
          {t('table.showing', {
            start: startEntry,
            end: endEntry,
            total: totalCount,
          })}
        </span>
      </div>

      <div className="flex items-center gap-4">
        {onPageSizeChange && (
          <div className="flex items-center gap-2">
            <span>{t('table.rowsPerPage')}:</span>
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="bg-surface-elevated border border-surface-border rounded px-2 py-1 text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="flex items-center gap-1.5">
          <span className="mr-2">
            {t('table.page')} {pageIndex + 1} {t('table.of')} {Math.max(1, pageCount)}
          </span>

          <button
            type="button"
            onClick={() => onPageChange(0)}
            disabled={!canPreviousPage}
            className="p-1 rounded border border-surface-border bg-surface hover:bg-surface-elevated disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            title="First Page"
          >
            <ChevronsLeft className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={() => onPageChange(pageIndex - 1)}
            disabled={!canPreviousPage}
            className="p-1 rounded border border-surface-border bg-surface hover:bg-surface-elevated disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            title="Previous Page"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={() => onPageChange(pageIndex + 1)}
            disabled={!canNextPage}
            className="p-1 rounded border border-surface-border bg-surface hover:bg-surface-elevated disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            title="Next Page"
          >
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={() => onPageChange(pageCount - 1)}
            disabled={!canNextPage}
            className="p-1 rounded border border-surface-border bg-surface hover:bg-surface-elevated disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            title="Last Page"
          >
            <ChevronsRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};
